import { useMediaQuery } from "@kyan/ui";
import { useEffect } from "react";
import { useDebounceValue } from "usehooks-ts";

import { defaultUnderlying } from "#/config/markets";
import { useOptionSlippage } from "#/hooks/useOptionSlippage";
import { useValidateOptionSize } from "#/hooks/useValidateOptionSize";
import { useIsOptionHedged } from "#/store/options/tradePanel/isOptionHedged";
import { useOption } from "#/store/options/tradePanel/option";
import { useOptionLimitPrice } from "#/store/options/tradePanel/optionLimitPrice";
import { useOptionMarketOrderType } from "#/store/options/tradePanel/optionMarketOrderType";
import { useOptionSize } from "#/store/options/tradePanel/optionSize";
import { type MarketOrder } from "#/types/orders";

import { useValidateOptionLimitPrice } from "../../../../hooks/useValidateOptionLimitPrice";
import { useValidateOptionLiquidity } from "./useValidateOptionLiquidity";

export const useOptionMarketOrderController = () => {
  const { option } = useOption();
  const { isSm } = useMediaQuery();
  const { optionSize, setOptionSize } = useOptionSize();
  const { isOptionHedged } = useIsOptionHedged();
  const { optionLimitPrice, setOptionLimitPrice } = useOptionLimitPrice();
  const { optionMarketOrderType, setOptionMarketOrderType } =
    useOptionMarketOrderType();

  const isBuySide = !option || option.isBuy;
  const underlying = option?.underlying ?? defaultUnderlying;

  const {
    optionSlippageFactor,
    priceDisplayPrecisionNotional,
    sizeDisplayPrecision,
  } = useOptionSlippage(option);
  const isMarkPriceReady = Boolean(option?.markPrice);
  const isSlippageFactorReady = Boolean(optionSlippageFactor);

  const instrumentName = option?.instrument_name;

  // Placeholder logic before Options Updater:
  const [debouncedLimitPrice] = useDebounceValue(optionLimitPrice, 800);
  const [debouncedOptionSize] = useDebounceValue(optionSize, 800);

  const { limitPriceError } = useValidateOptionLimitPrice({
    option,
    limitPrice: optionLimitPrice,
  });
  const sizeError = useValidateOptionSize({
    size: debouncedOptionSize,
    underlying,
  });
  const { liquidityInfo, liquidityError } = useValidateOptionLiquidity({
    option,
    limitPrice: debouncedLimitPrice,
    size: debouncedOptionSize,
  });

  const handleToggleSubOrderType = (id: MarketOrder) => {
    setOptionMarketOrderType(id);
  };

  useEffect(() => {
    if (!isMarkPriceReady || !isSlippageFactorReady) return;

    const markPrice = option?.markPrice || 1;
    const slippageMultiplier = isBuySide
      ? 1 + optionSlippageFactor
      : 1 - optionSlippageFactor;
    const defaultPriceWithSlippage = (markPrice * slippageMultiplier).toFixed(
      priceDisplayPrecisionNotional,
    );

    setOptionLimitPrice(defaultPriceWithSlippage);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we want to update price only once per new instrument direction;
  }, [
    instrumentName,
    isMarkPriceReady,
    isBuySide,
    optionSlippageFactor,
    isSlippageFactorReady,
  ]);

  return {
    isSm,
    sizeError,
    isBuySide,
    optionSize,
    liquidityInfo,
    isOptionHedged,
    liquidityError,
    limitPriceError,
    optionLimitPrice,
    sizeDisplayPrecision,
    optionMarketOrderType,
    priceDisplayPrecisionNotional,
    setOptionLimitPrice,
    setOptionSize,
    handleToggleSubOrderType,
  };
};
